import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { Theme } from 'markcat-domain';
import { configureApiClient } from './apiClient';
import { syncThemeColorMeta } from './stores/ui';
import { App } from './App';
// Side-effect: attach the beforeinstallprompt/appinstalled listeners at load,
// before the browser fires them (the event is one-shot and early).
import './installPrompt';

import 'markcat-design/tokens.css';
import 'markcat-design/themes.css';
import 'markcat-design/base.css';
import 'markcat-design/layout.css';
import 'markcat-design/animations.css';
import 'markcat-design/scrollbar.css';
import './global.css';

// Apply saved theme
const savedTheme = localStorage.getItem('mc_theme') || 'system';
document.documentElement.setAttribute('data-theme', savedTheme);
syncThemeColorMeta(savedTheme as Theme);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Dev: empty string = same origin, Vite proxy forwards /api to 127.0.0.1:8080.
// Prod: VITE_API_URL=https://api.markcat.app pins the API host so fetches from
// the markcat.app bundle don't hit the wrong origin. The HttpOnly refresh
// cookie crosses origins via REFRESH_COOKIE_DOMAIN=.markcat.app.
const apiBaseUrl = import.meta.env.VITE_API_URL ?? '';

configureApiClient(queryClient, apiBaseUrl);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
